import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Server, 
  Shield, 
  HardDrive, 
  Activity, 
  AlertTriangle, 
  CheckCircle,
  Clock,
  Wifi,
  WifiOff,
  Bell,
  User,
  Info,
  MoreVertical,
  Share2,
  X,
  Calendar,
  EyeOff,
  Eye,
  GripVertical,
  MapPin,
  ClipboardList
} from 'lucide-react';
import { dashboardService } from '../services/dashboardService';
import { plannerService } from '../services/plannerService';
import userService from '../services/userService';
import LoadingSpinner from '../components/LoadingSpinner';
import FarmMap from '../components/FarmMap';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [shareMenuOpen, setShareMenuOpen] = useState({});
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState({}); // { blockId: [userId1, userId2, ...] }
  const [hiddenBlocks, setHiddenBlocks] = useState(() => {
    // Wczytaj ukryte bloki z localStorage
    try {
      const stored = localStorage.getItem(`dashboard-hidden-blocks-${user?._id}`);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  
  const [blockOrder, setBlockOrder] = useState(() => {
    // Wczytaj kolejność bloków z localStorage
    try {
      const stored = localStorage.getItem(`dashboard-block-order-${user?._id}`);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  
  const [draggedBlockIndex, setDraggedBlockIndex] = useState(null);
  const [dragOverBlockIndex, setDragOverBlockIndex] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: '', onConfirm: null });
  
  const isSuperAdmin = user?.role === 'super admin';
  
  const { data: overview, isLoading: overviewLoading } = useQuery(
    'dashboard-overview',
    dashboardService.getOverview,
    { refetchInterval: 30000 }
  );

  const { data: quickActions, isLoading: actionsLoading } = useQuery(
    'quick-actions',
    dashboardService.getQuickActions
  );

  const { data: notifications, isLoading: notificationsLoading } = useQuery(
    'notifications',
    dashboardService.getNotifications,
    { refetchInterval: 60000, notifyOnChangeProps: ['data', 'error'] }
  );

  // Pobierz ogłoszenia z terminami
  const { data: announcementsData, isLoading: announcementsLoading } = useQuery(
    'dashboard-announcements',
    dashboardService.getAnnouncements,
    { refetchInterval: 60000 }
  );

  const canSeeFarms =
    isSuperAdmin ||
    user?.role === 'admin' ||
    user?.moduleAccess?.mapa ||
    user?.moduleAccess?.planner ||
    user?.moduleAccess?.farmyFoto;

  const { data: farmsData, isLoading: farmsLoading } = useQuery(
    'dashboard-farms',
    plannerService.getFarms,
    {
      enabled: !!canSeeFarms,
      staleTime: 5 * 60 * 1000,
      refetchInterval: false,
      refetchOnWindowFocus: false
    }
  );

  const todayIso = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  })();

  const { data: todayTasksData } = useQuery(
    ['dashboard-tasks-today', todayIso],
    () => plannerService.getTasks(todayIso, todayIso),
    {
      enabled: !!canSeeFarms,
      refetchInterval: 60000,
      staleTime: 30000
    }
  );

  const farms = useMemo(() => farmsData?.farms || [], [farmsData?.farms]);
  const todayTasks = useMemo(() => todayTasksData?.tasks || [], [todayTasksData?.tasks]);

  // Pobierz udostępnienia bloków
  const { data: blockSharesData } = useQuery(
    'dashboard-block-shares',
    dashboardService.getBlockShares,
    { enabled: !isSuperAdmin } // Tylko dla użytkowników, super admin widzi wszystko
  );

  // Pobierz listę użytkowników (tylko dla super admina)
  const { data: usersData } = useQuery(
    'users-list',
    userService.getUsers,
    { enabled: isSuperAdmin }
  );

  const shareBlockMutation = useMutation(
    ({ blockId, userIds }) => dashboardService.shareBlock(blockId, userIds),
    {
      onSuccess: (data) => {
        const count = data?.shares?.length || 0;
        toast.success(`Blok został udostępniony ${count} użytkownikom`);
        queryClient.invalidateQueries('dashboard-block-shares');
        setShareMenuOpen({});
        setSelectedBlockId(null);
        setSelectedUserIds({});
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas udostępniania bloku');
      }
    }
  );

  const unshareBlockMutation = useMutation(
    ({ blockId, userId }) => dashboardService.unshareBlock(blockId, userId),
    {
      onSuccess: (data) => {
        const message = data?.removedCount 
          ? `Usunięto dostęp dla ${data.removedCount} użytkowników`
          : 'Udostępnienie zostało usunięte';
        toast.success(message);
        queryClient.invalidateQueries('dashboard-block-shares');
        queryClient.invalidateQueries(['dashboard-block-shares']);
        setShareMenuOpen({});
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas usuwania udostępnienia');
      }
    }
  );

  const handleShareBlock = (blockId) => {
    const userIds = selectedUserIds[blockId] || [];
    if (userIds.length === 0) {
      toast.error('Wybierz przynajmniej jednego użytkownika');
      return;
    }
    shareBlockMutation.mutate({ blockId, userIds });
  };

  const handleUnshareBlock = (blockId, userId) => {
    unshareBlockMutation.mutate({ blockId, userId });
  };

  const handleUnshareAllBlocks = (blockId) => {
    setConfirmDialog({
      open: true,
      message: 'Czy na pewno chcesz usunąć dostęp do tego bloku wszystkim użytkownikom?',
      onConfirm: () => {
        unshareBlockMutation.mutate({ blockId, userId: null });
        setConfirmDialog({ open: false, message: '', onConfirm: null });
      }
    });
  };

  const handleToggleUserSelection = (blockId, userId, selectAll = false) => {
    if (selectAll) {
      // Zaznacz wszystkich użytkowników
      const allUserIds = usersData?.users?.map(u => u._id) || [];
      setSelectedUserIds(prev => ({
        ...prev,
        [blockId]: allUserIds
      }));
    } else {
      // Przełącz zaznaczenie pojedynczego użytkownika
      setSelectedUserIds(prev => {
        const current = prev[blockId] || [];
        const isSelected = current.includes(userId);
        return {
          ...prev,
          [blockId]: isSelected
            ? current.filter(id => id !== userId)
            : [...current, userId]
        };
      });
    }
  };

  const handleHideBlock = (blockId) => {
    const newHiddenBlocks = [...hiddenBlocks, blockId];
    setHiddenBlocks(newHiddenBlocks);
    try {
      localStorage.setItem(`dashboard-hidden-blocks-${user?._id}`, JSON.stringify(newHiddenBlocks));
      toast.success('Blok został ukryty');
    } catch (error) {
      console.error('Błąd zapisu do localStorage:', error);
    }
  };

  const handleShowBlock = (blockId) => {
    const newHiddenBlocks = hiddenBlocks.filter(id => id !== blockId);
    setHiddenBlocks(newHiddenBlocks);
    try {
      localStorage.setItem(`dashboard-hidden-blocks-${user?._id}`, JSON.stringify(newHiddenBlocks));
      toast.success('Blok został pokazany');
    } catch (error) {
      console.error('Błąd zapisu do localStorage:', error);
    }
  };

  // Sprawdź czy blok jest widoczny dla użytkownika (nie ukryty)
  const isBlockVisible = (blockId) => {
    if (isSuperAdmin) {
      // Super admin widzi wszystko oprócz ukrytych przez siebie
      return !hiddenBlocks.includes(blockId);
    }
    const sharedBlocks = blockSharesData?.sharedBlocks || [];
    // Blok jest widoczny jeśli jest udostępniony i nie jest ukryty
    return sharedBlocks.includes(blockId) && !hiddenBlocks.includes(blockId);
  };

  // Sprawdź czy blok jest udostępniony (dla zwykłych użytkowników)
  const isBlockShared = (blockId) => {
    if (isSuperAdmin) return true;
    const sharedBlocks = blockSharesData?.sharedBlocks || [];
    return sharedBlocks.includes(blockId);
  };

  // Sprawdź czy blok jest ukryty
  const isBlockHidden = (blockId) => {
    return hiddenBlocks.includes(blockId);
  };

  // Funkcje drag and drop dla bloków
  const handleDragStart = (e, index) => {
    setDraggedBlockIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedBlockIndex(null);
    setDragOverBlockIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverBlockIndex !== index) {
      setDragOverBlockIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverBlockIndex(null);
  };

  const handleDrop = (e, dropIndex, currentBlocks) => {
    e.preventDefault();
    if (draggedBlockIndex === null || draggedBlockIndex === dropIndex) {
      setDragOverBlockIndex(null);
      return;
    }

    const newOrder = [...currentBlocks];
    const draggedBlock = newOrder[draggedBlockIndex];
    newOrder.splice(draggedBlockIndex, 1);
    newOrder.splice(dropIndex, 0, draggedBlock);

    // Zapisz kolejność w localStorage
    const blockIds = newOrder.map(block => {
      // Dla statystyk, użyj blockId z komponentu
      if (block.type === 'stat' && React.isValidElement(block.component)) {
        const blockId = block.component.props?.blockId || block.component.key;
        if (blockId) return blockId;
      }
      return block.type;
    });
    setBlockOrder(blockIds);
    try {
      localStorage.setItem(`dashboard-block-order-${user?._id}`, JSON.stringify(blockIds));
      toast.success('Kolejność bloków została zapisana');
    } catch (error) {
      console.error('Błąd zapisu kolejności bloków:', error);
    }

    setDraggedBlockIndex(null);
    setDragOverBlockIndex(null);
  };

  // Sortuj bloki według zapisanej kolejności
  const sortBlocksByOrder = (blocks) => {
    if (!blockOrder || blockOrder.length === 0) return blocks;
    
    const sorted = [...blocks];
    sorted.sort((a, b) => {
      const aId = a.type === 'stat' && a.component?.props?.blockId 
        ? a.component.props.blockId 
        : a.type;
      const bId = b.type === 'stat' && b.component?.props?.blockId 
        ? b.component.props.blockId 
        : b.type;
      const aIndex = blockOrder.indexOf(aId);
      const bIndex = blockOrder.indexOf(bId);
      
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
    
    return sorted;
  };

  if (overviewLoading || actionsLoading || notificationsLoading || announcementsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const stats = [
    {
      id: 'stats-devices',
      name: 'Wszystkie urządzenia',
      value: overview?.overview?.totalDevices || 0,
      icon: Server,
      iconColor: 'text-sky-400',
      iconBg: 'bg-sky-500/15'
    },
    {
      id: 'stats-online',
      name: 'Urządzenia online',
      value: overview?.overview?.onlineDevices || 0,
      icon: Wifi,
      iconColor: 'text-emerald-400',
      iconBg: 'bg-emerald-500/15'
    },
    {
      id: 'stats-vpn',
      name: 'Połączenia VPN',
      value: overview?.overview?.totalVPNConnections || 0,
      icon: Shield,
      iconColor: 'text-violet-400',
      iconBg: 'bg-violet-500/15'
    },
    {
      id: 'stats-drives',
      name: 'Zmapowane dyski',
      value: overview?.overview?.mappedDrives || 0,
      icon: HardDrive,
      iconColor: 'text-orange-400',
      iconBg: 'bg-orange-500/15'
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
      case 'connected':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'offline':
      case 'disconnected':
        return <WifiOff className="h-4 w-4 text-red-500" />;
      case 'connecting':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'online': return 'Online';
      case 'offline': return 'Offline';
      case 'connected': return 'Połączony';
      case 'disconnected': return 'Rozłączony';
      case 'connecting': return 'Łączenie...';
      case 'error': return 'Błąd';
      default: return 'Nieznany';
    }
  };

  // Komponent bloku z menu udostępniania
  const BlockWithMenu = ({ blockId, children, className = '' }) => {
    // Pobierz użytkowników którzy mają udostępniony ten blok (tylko dla super admina)
    const { data: sharedUsersData } = useQuery(
      ['dashboard-block-shares', blockId],
      () => dashboardService.getBlockShares(blockId),
      { enabled: isSuperAdmin && shareMenuOpen[blockId] }
    );

    const currentSelectedIds = selectedUserIds[blockId] || [];
    const availableUsers = usersData?.users?.filter(u => 
      !sharedUsersData?.sharedUsers?.some(su => su.userId === u._id)
    ) || [];
    const allUserIds = availableUsers.map(u => u._id);
    const allSelected = allUserIds.length > 0 && allUserIds.every(id => currentSelectedIds.includes(id));

    const isHidden = isBlockHidden(blockId);
    const isShared = isBlockShared(blockId);

    if (!isSuperAdmin) {
      // Dla zwykłych użytkowników sprawdź czy blok jest udostępniony
      if (!isShared) {
        return null; // Nie pokazuj bloku jeśli nie jest udostępniony
      }
      
      // Jeśli blok jest ukryty, pokaż jako placeholder z możliwością odkrycia
      if (isHidden) {
        return (
          <div className={`relative ${className} opacity-30 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg`}>
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50 rounded-lg">
              <button
                onClick={() => handleShowBlock(blockId)}
                className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors flex items-center space-x-2 shadow-lg"
                title="Odkryj blok"
              >
                <Eye className="h-4 w-4" />
                <span>Odkryj blok</span>
              </button>
            </div>
            {children}
          </div>
        );
      }

      return (
        <div className={`relative ${className}`}>
          {children}
          {/* Przycisk ukrycia dla zwykłych użytkowników */}
          <div className="absolute top-2 right-2">
            <button
              onClick={() => handleHideBlock(blockId)}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              title="Ukryj blok"
            >
              <EyeOff className="h-4 w-4 text-gray-500" />
            </button>
          </div>
        </div>
      );
    }

    // Jeśli blok jest ukryty, pokaż jako placeholder z możliwością odkrycia
    if (isHidden) {
      return (
        <div className={`relative ${className} opacity-30 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg`}>
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50 rounded-lg z-10">
            <button
              onClick={() => handleShowBlock(blockId)}
              className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors flex items-center space-x-2 shadow-lg"
              title="Odkryj blok"
            >
              <Eye className="h-4 w-4" />
              <span>Odkryj blok</span>
            </button>
          </div>
          {children}
        </div>
      );
    }

    return (
      <div className={`relative ${className}`}>
        {children}
        {/* Menu udostępniania - 3 kropki */}
        <div className="absolute top-2 right-2 flex items-center space-x-1">
          {/* Przycisk ukrycia dla super admina */}
          <button
            onClick={() => handleHideBlock(blockId)}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            title="Ukryj blok"
          >
            <EyeOff className="h-4 w-4 text-gray-500" />
          </button>
          
          {/* Menu udostępniania */}
          <div className="relative">
            <button
              onClick={() => {
                setShareMenuOpen(prev => ({ ...prev, [blockId]: !prev[blockId] }));
                if (!shareMenuOpen[blockId]) {
                  queryClient.invalidateQueries(['dashboard-block-shares', blockId]);
                }
              }}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            >
              <MoreVertical className="h-4 w-4 text-gray-500" />
            </button>
            
            {shareMenuOpen[blockId] && (
              <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-y-auto">
                <div className="p-3">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-gray-900">Udostępnij blok</span>
                    <button
                      onClick={() => setShareMenuOpen(prev => ({ ...prev, [blockId]: false }))}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {/* Lista użytkowników którzy mają udostępniony blok */}
                  {sharedUsersData?.sharedUsers && sharedUsersData.sharedUsers.length > 0 && (
                    <div className="mb-3 pb-3 border-b border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-gray-700">Udostępnione dla:</p>
                        <button
                          onClick={() => handleUnshareAllBlocks(blockId)}
                          disabled={unshareBlockMutation.isLoading}
                          className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50 flex items-center space-x-1"
                          title="Usuń dostęp wszystkim użytkownikom"
                        >
                          <X className="h-3 w-3" />
                          <span>Usuń wszystkim</span>
                        </button>
                      </div>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {sharedUsersData.sharedUsers.map((sharedUser) => (
                          <div key={sharedUser.userId} className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                            <span className="text-gray-700">
                              {sharedUser.firstName && sharedUser.lastName 
                                ? `${sharedUser.firstName} ${sharedUser.lastName}`
                                : sharedUser.username}
                            </span>
                            <button
                              onClick={() => handleUnshareBlock(blockId, sharedUser.userId)}
                              className="text-red-600 hover:text-red-800"
                              title="Usuń udostępnienie"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Lista użytkowników do wyboru z checkboxami */}
                  {availableUsers.length > 0 && (
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-gray-700">Wybierz użytkowników:</p>
                        <button
                          onClick={() => handleToggleUserSelection(blockId, null, true)}
                          className="text-xs text-primary-600 hover:text-primary-800"
                        >
                          {allSelected ? 'Odznacz wszystkich' : 'Zaznacz wszystkich'}
                        </button>
                      </div>
                      <div className="space-y-1 max-h-48 overflow-y-auto border border-gray-200 rounded p-2">
                        {availableUsers.map((u) => {
                          const isSelected = currentSelectedIds.includes(u._id);
                          return (
                            <label
                              key={u._id}
                              className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleUserSelection(blockId, u._id)}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-700 flex-1">
                                {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.username}
                                <span className="text-xs text-gray-500 ml-1">({u.email})</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={() => handleShareBlock(blockId)}
                    disabled={currentSelectedIds.length === 0 || shareBlockMutation.isLoading}
                    className="w-full px-3 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 text-sm flex items-center justify-center space-x-2"
                  >
                    <Share2 className="h-4 w-4" />
                    <span>
                      {shareBlockMutation.isLoading 
                        ? 'Udostępnianie...' 
                        : `Udostępnij ${currentSelectedIds.length > 0 ? `(${currentSelectedIds.length})` : ''}`}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Komponenty bloków
  const StatsBlock = () => (
    <div className="stat-strip">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.id}
              className="flex items-center gap-4 px-5 py-5 lg:px-6 lg:py-6 min-w-0"
            >
              <div className={`p-3 rounded-xl ${stat.iconBg} flex-shrink-0`}>
                <Icon className={`h-6 w-6 ${stat.iconColor}`} />
              </div>
              <div className="min-w-0">
                <p className="text-3xl font-bold text-white tabular-nums leading-none">
                  {stat.value}
                </p>
                <p className="text-xs font-medium text-white/50 mt-1.5 truncate">
                  {stat.name}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const RecentActivityBlock = () => (
    <BlockWithMenu blockId="recent-activity">
      <div className="card hover:shadow-lg transition-shadow duration-200">
        <div className="card-header">
          <h3 className="text-base font-semibold text-ink flex items-center">
            <Activity className="h-5 w-5 text-primary-600 mr-2" />
            Ostatnia aktywność
          </h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 gap-6">
            {/* Recent Devices */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                <Server className="h-4 w-4 text-primary-600 mr-2" />
                Urządzenia
              </h4>
              <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                {overview?.recentActivity?.devices?.map((device) => (
                  <div key={device._id} className="flex items-center justify-between p-3 rounded-xl bg-surface hover:bg-surface-muted transition-colors">
                    <div className="flex items-center min-w-0">
                      {getStatusIcon(device.status)}
                      <span className="ml-3 text-sm font-medium text-ink truncate">{device.name}</span>
                    </div>
                    <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold flex-shrink-0 ${
                      device.status === 'online' ? 'bg-emerald-100 text-emerald-800' :
                      device.status === 'offline' ? 'bg-red-100 text-red-800' :
                      'bg-amber-100 text-amber-800'
                    }`}>
                      {getStatusText(device.status)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent VPN Connections */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                <Shield className="h-4 w-4 text-purple-600 mr-2" />
                Połączenia VPN
              </h4>
              <div className="space-y-3">
                {overview?.recentActivity?.vpnConnections?.map((connection) => (
                  <div key={connection._id} className="flex items-center justify-between p-3 rounded-xl bg-surface hover:bg-surface-muted transition-colors">
                    <div className="flex items-center min-w-0">
                      {getStatusIcon(connection.status)}
                      <span className="ml-3 text-sm font-medium text-ink truncate">{connection.name}</span>
                    </div>
                    <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold flex-shrink-0 ${
                      connection.status === 'connected' ? 'bg-emerald-100 text-emerald-800' :
                      connection.status === 'disconnected' ? 'bg-red-100 text-red-800' :
                      'bg-amber-100 text-amber-800'
                    }`}>
                      {getStatusText(connection.status)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </BlockWithMenu>
  );

  const ActiveUsersBlock = () => (
    (user?.role === 'admin' || user?.role === 'super admin') && overview?.recentActivity?.activeSessions ? (
      <BlockWithMenu blockId="active-users">
        <div className="card hover:shadow-lg transition-shadow duration-200">
          <div className="card-header">
            <h3 className="text-base font-semibold text-ink flex items-center">
              <User className="h-5 w-5 text-primary-600 mr-2" />
              Aktywni użytkownicy
            </h3>
          </div>
          <div className="card-body">
            {overview.recentActivity.activeSessions.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {overview.recentActivity.activeSessions.map((session) => (
                  <div key={session._id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-green-600" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">{session.username}</p>
                        <p className="text-xs text-gray-600">{session.email}</p>
                        <p className="text-xs text-gray-500">
                          Ostatnia aktywność: {new Date(session.lastActivity).toLocaleString('pl-PL')}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        session.role === 'super admin'
                          ? 'bg-purple-100 text-purple-800'
                          : session.role === 'admin' 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {session.role === 'super admin' ? 'Super Admin' : session.role === 'admin' ? 'Admin' : 'User'}
                      </span>
                      <span className="text-xs text-green-600 mt-1">
                        ● Online
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <User className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Brak aktywnych użytkowników
                </h3>
                <p className="text-sm text-gray-600">
                  Obecnie nikt nie jest zalogowany
                </p>
              </div>
            )}
          </div>
        </div>
      </BlockWithMenu>
    ) : null
  );

  const NotificationsBlock = () => (
    <BlockWithMenu blockId="notifications">
      <div className="card hover:shadow-card-hover transition-shadow duration-200 h-full flex flex-col">
        <div className="card-header flex items-center justify-between">
          <h3 className="text-sm font-bold text-ink tracking-wide uppercase flex items-center">
            <Bell className="h-4 w-4 text-primary-600 mr-2" />
            Powiadomienia
          </h3>
          <Link to="/announcements" className="text-xs font-medium text-primary-600 hover:text-primary-700">
            Zobacz wszystkie →
          </Link>
        </div>
        <div className="card-body flex-1 overflow-hidden">
          {notifications?.notifications?.length > 0 ? (
            <div className="space-y-3 max-h-[380px] overflow-y-auto pr-1">
              {notifications.notifications.slice(0, 8).map((notification) => (
                <div
                  key={notification.id}
                  className="p-3 rounded-xl bg-primary-50/70 border border-primary-100/80 transition-all hover:bg-primary-50"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {notification.type === 'error' ? (
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                      ) : notification.type === 'warning' ? (
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                      ) : notification.type === 'success' ? (
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <Info className="h-4 w-4 text-primary-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-ink truncate">
                        {notification.title}
                      </p>
                      <p className="text-xs text-ink-soft mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-[11px] text-ink-faint mt-1.5">
                        {new Date(notification.timestamp).toLocaleString('pl-PL')}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <CheckCircle className="h-8 w-8 text-primary-500 mx-auto mb-2" />
              <p className="text-sm font-medium text-ink">Brak powiadomień</p>
              <p className="text-xs text-ink-faint mt-1">Wszystko działa poprawnie</p>
            </div>
          )}
        </div>
      </div>
    </BlockWithMenu>
  );

  // Stabilna mapa — bez BlockWithMenu / wewnętrznego komponentu (unikamy remountu Google Maps)
  const canAddFarm =
    isSuperAdmin || user?.role === 'admin' || !!user?.moduleAccess?.farmyFoto;

  const farmsMapElement = canSeeFarms ? (
    <FarmMap
      key="dashboard-farm-map"
      farms={farms}
      loading={farmsLoading}
      height={420}
      showAddButton={canAddFarm}
    />
  ) : null;

  const KeyIndicatorsBlock = () => {
    const overdue = (announcementsData?.announcements || []).filter((a) => a.diffDays < 0).length;
    const indicators = [
      {
        label: 'Urządzenia online',
        value: overview?.overview?.onlineDevices ?? 0,
        icon: Wifi,
        hint: `z ${overview?.overview?.totalDevices ?? 0} łącznie`,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50'
      },
      {
        label: 'Farmy w systemie',
        value: farms.length,
        icon: MapPin,
        hint: 'z lokalizacją GPS',
        color: 'text-accent-red',
        bg: 'bg-red-50'
      },
      {
        label: 'Zadania na dziś',
        value: todayTasks.length,
        icon: ClipboardList,
        hint: 'planer serwisowy',
        color: 'text-primary-600',
        bg: 'bg-primary-50'
      },
      {
        label: 'Połączenia VPN',
        value: overview?.overview?.totalVPNConnections ?? 0,
        icon: Shield,
        hint: 'aktywne / skonfigurowane',
        color: 'text-violet-600',
        bg: 'bg-violet-50'
      },
      {
        label: overdue > 0 ? 'Po terminie' : 'Ogłoszenia',
        value: overdue > 0 ? overdue : (announcementsData?.announcements?.length ?? 0),
        icon: overdue > 0 ? AlertTriangle : Calendar,
        hint: overdue > 0 ? 'terminy minęły' : 'najbliższe terminy',
        color: overdue > 0 ? 'text-red-600' : 'text-sky-600',
        bg: overdue > 0 ? 'bg-red-50' : 'bg-sky-50'
      }
    ];

    return (
      <div>
        <h3 className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-soft mb-3 px-0.5">
          Najważniejsze wskaźniki
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          {indicators.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.label} className="card !rounded-2xl px-4 py-4 border-black/[0.03]">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium text-ink-faint truncate">{item.label}</p>
                    <p className="text-2xl font-bold text-ink mt-1 tabular-nums">{item.value}</p>
                    <p className="text-[10px] text-ink-faint mt-1 truncate">{item.hint}</p>
                  </div>
                  <div className={`p-2 rounded-xl ${item.bg} flex-shrink-0`}>
                    <Icon className={`h-4 w-4 ${item.color}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const TasksTodayBlock = () => (
    canSeeFarms ? (
      <div className="card h-full flex flex-col">
        <div className="card-header flex items-center justify-between">
          <h3 className="text-base font-semibold text-ink flex items-center">
            <ClipboardList className="h-5 w-5 text-primary-600 mr-2" />
            Zadania na dziś
          </h3>
          <Link to="/planner/calendar" className="text-xs font-medium text-primary-600 hover:text-primary-700">
            Planer
          </Link>
        </div>
        <div className="card-body flex-1 overflow-hidden">
          {todayTasks.length > 0 ? (
            <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
              {todayTasks.slice(0, 8).map((task) => {
                const cards = task.kanbanCards || [];
                const doneCount = (task.taskCompletions || []).length;
                const inProgress = doneCount > 0 && doneCount < Math.max(cards.length, 1);
                const planned = doneCount === 0;
                return (
                  <div key={task._id} className="p-3 rounded-xl bg-surface border border-black/[0.04]">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-ink truncate">
                          {cards[0]?.title || task.farmName}
                        </p>
                        <p className="text-xs text-ink-soft mt-0.5 truncate">
                          {task.farmName}
                          {task.teamName ? ` · ${task.teamName}` : ''}
                        </p>
                        <p className="text-[11px] text-ink-faint mt-1">
                          {new Date(task.date).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                          {cards.length > 1 ? ` · ${cards.length} kart` : ''}
                        </p>
                      </div>
                      <span className={planned ? 'badge-planned' : inProgress ? 'badge-progress' : 'status-online'}>
                        {planned ? 'Planowane' : inProgress ? 'W toku' : 'Online'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-10">
              <ClipboardList className="h-8 w-8 text-ink-faint mx-auto mb-2" />
              <p className="text-sm text-ink-soft">Brak zadań na dziś</p>
            </div>
          )}
        </div>
      </div>
    ) : null
  );

  const AnnouncementsBanner = () => {
    const list = announcementsData?.announcements || [];
    if (!list.length) return null;
    const urgent = list.find((a) => a.diffDays < 0) || list[0];
    if (!urgent) return null;

    return (
      <div
        className={`rounded-2xl px-4 py-3 flex items-center justify-between gap-4 flex-wrap ${
          urgent.diffDays < 0
            ? 'bg-red-50 border border-red-200'
            : 'bg-amber-50 border border-amber-200'
        }`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${urgent.diffDays < 0 ? 'text-red-600' : 'text-amber-600'}`} />
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wide text-ink-faint">Ogłoszenie</p>
            <p className="text-sm font-semibold text-ink truncate">{urgent.title}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-ink-soft hidden sm:inline">
            {new Date(urgent.eventDate).toLocaleDateString('pl-PL')}
          </span>
          <span
            className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
              urgent.diffDays < 0 ? 'bg-red-600 text-white' : 'bg-amber-200 text-amber-900'
            }`}
          >
            {urgent.timeToDeadline}
          </span>
          <Link to="/announcements" className="text-xs font-medium text-primary-700 hover:underline">
            Szczegóły
          </Link>
        </div>
      </div>
    );
  };

  const QuickActionsBlock = () => (
    <BlockWithMenu blockId="quick-actions">
      <div className="card hover:shadow-lg transition-shadow duration-200">
        <div className="card-header">
          <h3 className="text-base font-semibold text-ink flex items-center">
            <Shield className="h-5 w-5 text-primary-600 mr-2" />
            Szybkie akcje
          </h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {quickActions?.quickActions?.map((action) => (
              <button
                key={action.id}
                disabled={!action.available}
                className={`p-4 text-left rounded-xl border-2 transition-all duration-200 ${
                  action.available
                    ? 'border-gray-200 hover:border-primary-300 hover:bg-primary-50 hover:shadow-md transform hover:-translate-y-1'
                    : 'border-gray-100 bg-gray-50 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center">
                  <div className={`p-3 rounded-lg ${
                    action.available ? 'bg-primary-100' : 'bg-gray-100'
                  }`}>
                    <Shield className={`h-5 w-5 ${
                      action.available ? 'text-primary-600' : 'text-gray-400'
                    }`} />
                  </div>
                  <div className="ml-4">
                    <p className={`text-sm font-semibold ${
                      action.available ? 'text-gray-900' : 'text-gray-500'
                    }`}>
                      {action.title}
                    </p>
                    <p className={`text-xs mt-1 ${
                      action.available ? 'text-gray-600' : 'text-gray-400'
                    }`}>
                      {action.description}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </BlockWithMenu>
  );

  // Zbierz wszystkie bloki dla zwykłych użytkowników (włączając ukryte jako placeholdery)
  const visibleBlocks = [];
  
  // Stats - każdy stat jako osobny blok dla zwykłych użytkowników
  if (isSuperAdmin || stats.some(stat => isBlockShared(stat.id))) {
    if (isSuperAdmin) {
      // Dla super admina pokaż wszystkie stats razem
      visibleBlocks.push({ type: 'stats', component: <StatsBlock key="stats" /> });
    } else {
      // Dla zwykłych użytkowników pokaż każdy stat osobno jeśli jest udostępniony (włączając ukryte)
      stats.forEach(stat => {
        if (isBlockShared(stat.id)) {
          const Icon = stat.icon;
          visibleBlocks.push({
            type: 'stat',
            component: (
              <BlockWithMenu key={stat.id} blockId={stat.id}>
                <div className="card hover:shadow-lg transition-shadow duration-200">
                  <div className="card-body">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className={`p-3 rounded-lg ${stat.iconBg || 'bg-primary-100'} transition-transform duration-200 hover:scale-110`}>
                          <Icon className={`h-6 w-6 ${stat.iconColor || 'text-primary-600'}`} />
                        </div>
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            {stat.name}
                          </dt>
                          <dd className="text-2xl font-bold text-gray-900">
                            {stat.value}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              </BlockWithMenu>
            )
          });
        }
      });
    }
  }

  if (isSuperAdmin || isBlockShared('recent-activity')) {
    visibleBlocks.push({ type: 'recent-activity', component: <RecentActivityBlock key="recent-activity" /> });
  }

  if (isSuperAdmin || isBlockShared('notifications')) {
    visibleBlocks.push({ type: 'notifications', component: <NotificationsBlock key="notifications" /> });
  }

  // Aktywni użytkownicy - tylko dla adminów i super adminów
  if ((isSuperAdmin || isBlockShared('active-users')) && (user?.role === 'admin' || user?.role === 'super admin')) {
    const activeUsersBlock = <ActiveUsersBlock key="active-users" />;
    if (activeUsersBlock) {
      visibleBlocks.push({ type: 'active-users', component: activeUsersBlock });
    }
  }

  if (isSuperAdmin || isBlockShared('announcements')) {
    const announcementsBlock = <AnnouncementsBanner key="announcements" />;
    if (announcementsBlock) {
      visibleBlocks.push({ type: 'announcements', component: announcementsBlock });
    }
  }

  if (canSeeFarms && !isSuperAdmin) {
    visibleBlocks.unshift({ type: 'farms-map', component: farmsMapElement });
  }

  if (isSuperAdmin || isBlockShared('quick-actions')) {
    visibleBlocks.push({ type: 'quick-actions', component: <QuickActionsBlock key="quick-actions" /> });
  }

  // Komponent modala potwierdzenia
  const ConfirmDialog = () => {
    if (!confirmDialog.open) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="ml-3 text-lg font-semibold text-gray-900">
                Potwierdzenie
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              {confirmDialog.message}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setConfirmDialog({ open: false, message: '', onConfirm: null })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Anuluj
              </button>
              <button
                onClick={() => {
                  if (confirmDialog.onConfirm) {
                    confirmDialog.onConfirm();
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Renderowanie dla super admina — układ jak mockup (mapa + wskaźniki)
  if (isSuperAdmin) {
    return (
      <>
        <div className="space-y-5">
          <StatsBlock />

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
            <div className="xl:col-span-2 min-h-[440px]">
              {farmsMapElement}
            </div>
            <div className="min-h-[440px]">
              <NotificationsBlock />
            </div>
          </div>

          <KeyIndicatorsBlock />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <RecentActivityBlock />
            <TasksTodayBlock />
            {(user?.role === 'admin' || user?.role === 'super admin') && <ActiveUsersBlock />}
          </div>

          <AnnouncementsBanner />
          <QuickActionsBlock />
        </div>
        <ConfirmDialog />
      </>
    );
  }

  // Renderowanie dla zwykłych użytkowników (wszystkie bloki w siatce obok siebie)
  const sortedBlocks = sortBlocksByOrder(visibleBlocks);
  
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedBlocks.map((block, index) => (
          <div
            key={index}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index, sortedBlocks)}
            className={`relative transition-all ${
              draggedBlockIndex === index ? 'opacity-50' : ''
            } ${
              dragOverBlockIndex === index ? 'ring-2 ring-primary-500 ring-offset-2' : ''
            }`}
          >
            {/* Ikona do przeciągania */}
            <div className="absolute top-2 left-2 z-10 cursor-move opacity-0 hover:opacity-100 transition-opacity group">
              <GripVertical className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
            </div>
            {block.component}
          </div>
        ))}
      </div>
      <ConfirmDialog />
    </>
  );
};

export default Dashboard;
